<template>
  <b-modal
    ref="dfCorrectionModal"
    size="lg"
    centered
    no-close-on-backdrop
    @hide="closeModal"
    @hidden="resetData"
  >
    <template #modal-title>
      <i class="fas fa-edit mr-2"></i>แก้ไข DF
    </template>

    <b-card class="mb-3" no-body>
      <b-card-header class="py-2 bg-light">ข้อมูลปัจจุบัน</b-card-header>
      <b-card-body class="py-2">
        <b-row>
          <b-col cols="6"
            ><strong>คนไข้:</strong>
            {{ currentItem.patientFullName }}</b-col
          >
          <b-col cols="6"
            ><strong>DN:</strong> {{ currentItem.dn }}</b-col
          >
        </b-row>
        <b-row class="mt-1">
          <b-col cols="6"
            ><strong>รายการรักษา:</strong>
            {{ currentItem.itemName }}</b-col
          >
          <b-col cols="6"
            ><strong>วันที่:</strong>
            {{ formatDate(currentItem.creationDt) }}</b-col
          >
        </b-row>
        <b-row class="mt-1">
          <b-col cols="4"
            ><strong>ชำระ:</strong> {{ currentItem.paid }}</b-col
          >
          <b-col cols="4"
            ><strong>DF ปัจจุบัน:</strong>
            {{ currentItem.finalDf }}</b-col
          >
          <b-col cols="4"
            ><strong>CF ปัจจุบัน:</strong>
            {{ currentItem.finalCf }}</b-col
          >
        </b-row>
      </b-card-body>
    </b-card>

    <b-card no-body>
      <b-card-header class="py-2 bg-light">ข้อมูลแก้ไข</b-card-header>
      <b-card-body class="py-2">
        <b-form-group
          label="แพทย์:"
          label-cols-md="3"
          label-align-md="right"
          class="mb-2"
          label-size="sm"
        >
          <b-form-select
            v-model="form.correctedActionUid"
            :options="doctorOptions"
            text-field="fullName"
            value-field="uid"
            size="sm"
          >
            <template #first>
              <b-form-select-option :value="null" disabled
                >-- เลือกแพทย์ --</b-form-select-option
              >
            </template>
          </b-form-select>
        </b-form-group>

        <b-form-group
          label="ค่าหมอ (฿):"
          label-cols-md="3"
          label-align-md="right"
          class="mb-2"
          label-size="sm"
          :invalid-feedback="dfInvalidFeedback"
          :state="dfState"
        >
          <b-input-group append="฿" size="sm">
            <b-form-input
              v-model.number="form.correctedDf"
              size="sm"
              :state="dfState"
              min="0"
              placeholder="ระบุค่าหมอใหม่ (บาท)"
            />
          </b-input-group>
          <small
            v-if="dfExceedsAmountNotice"
            class="text-info d-block mt-1"
          >
            <i class="fas fa-info-circle mr-1"></i>
            ค่าหมอมากกว่ายอดในใบเสร็จ — คลินิกจะรับภาระส่วนต่าง
            ({{ formatMoney(clinicAbsorbedAmount) }} ฿)
          </small>
        </b-form-group>

        <b-form-group
          label="หมายเหตุ*:"
          label-cols-md="3"
          label-align-md="right"
          class="mb-2"
          label-size="sm"
          :invalid-feedback="'กรุณาระบุหมายเหตุ'"
          :state="remarkState"
        >
          <b-form-textarea
            v-model="form.remark"
            size="sm"
            rows="2"
            :state="remarkState"
            placeholder="ระบุเหตุผลในการแก้ไข (จำเป็น)"
          />
        </b-form-group>
      </b-card-body>
    </b-card>

    <template #modal-footer>
      <b-button variant="secondary" @click="hide">ยกเลิก</b-button>
      <b-button
        variant="primary"
        :disabled="!isFormValid || isSaving"
        @click="save"
      >
        <b-spinner v-if="isSaving" small class="mr-1" />
        บันทึก
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import moment from "moment";

export default {
  name: "DfCorrectionModal",
  data() {
    return {
      currentItem: {},
      doctorOptions: [],
      originalActionUid: null,
      form: {
        correctedDf: null,
        correctedActionUid: null,
        remark: "",
      },
      isSaving: false,
      submitted: false,
    };
  },
  computed: {
    dfState() {
      if (!this.submitted) return null;
      if (this.form.correctedDf === null || this.form.correctedDf === "")
        return false;
      if (this.form.correctedDf < 0) return false;
      return true;
    },
    dfInvalidFeedback() {
      if (this.form.correctedDf === null || this.form.correctedDf === "")
        return "กรุณาระบุค่าหมอ";
      if (this.form.correctedDf < 0) return "ค่าหมอต้องไม่ต่ำกว่า 0";
      return "";
    },
    remarkState() {
      if (!this.submitted) return null;
      return this.form.remark.trim().length > 0;
    },
    isFormValid() {
      const dfValid =
        this.form.correctedDf !== null &&
        this.form.correctedDf !== "" &&
        this.form.correctedDf >= 0;
      const remarkValid = this.form.remark.trim().length > 0;
      return dfValid && remarkValid;
    },
    itemPaid() {
      return Number(this.currentItem.paid) || 0;
    },
    clinicAbsorbedAmount() {
      const df = Number(this.form.correctedDf) || 0;
      return Math.max(0, df - this.itemPaid);
    },
    dfExceedsAmountNotice() {
      const df = Number(this.form.correctedDf) || 0;
      return df > this.itemPaid;
    },
  },
  methods: {
    show(item, doctorsList) {
      this.currentItem = { ...item };
      this.doctorOptions = doctorsList || [];
      const matchedDoctor = this.doctorOptions.find(
        d => d.fullName === item.doctorFullName
      );
      const doctorUid = matchedDoctor ? matchedDoctor.uid : null;
      this.originalActionUid = doctorUid;
      this.form.correctedActionUid = doctorUid;
      this.$refs.dfCorrectionModal.show();
    },
    hide() {
      this.$refs.dfCorrectionModal.hide();
    },
    closeModal() {},
    resetData() {
      this.currentItem = {};
      this.form = {
        correctedDf: null,
        correctedActionUid: null,
        remark: "",
      };
      this.submitted = false;
      this.isSaving = false;
    },
    formatDate(date) {
      if (date) {
        return moment(date).locale("th").format("D MMM YY");
      }
      return "";
    },
    formatMoney(num) {
      return Number(num || 0).toLocaleString("th-TH", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    async save() {
      this.submitted = true;
      if (!this.isFormValid) return;

      // ไม่มีด่านรอบปิดบัญชีตรงนี้โดยตั้งใจ — BE บังคับเองที่ createDfCorrection
      // และคืน 400 ซึ่ง catch ด้านล่างส่งข้อความจริงจาก BE ต่อให้ผู้ใช้เห็น
      this.isSaving = true;
      const payload = {
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
        receiptId: this.currentItem.receiptId || this.currentItem.id,
        receiptItemId: this.currentItem.receiptItemId,
        correctedDf: this.form.correctedDf,
        correctedDfType: 1,
        remark: this.form.remark,
      };
      if (this.form.correctedActionUid !== this.originalActionUid) {
        payload.correctedActionUid = this.form.correctedActionUid;
      }

      try {
        await this.$store.dispatch(
          "moduleDfCorrection/createDfCorrection",
          payload
        );
        this.$emit("correctionSaved");
        this.hide();
      } catch (err) {
        const status = err?.response?.status;
        if (status === 400) {
          const msg =
            err?.response?.data?.message ||
            "ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบอีกครั้ง";
          this.$emit("error", msg);
        } else if (status === 409) {
          this.$emit("error", "มีการแก้ไขซ้ำ กรุณาลองใหม่อีกครั้ง");
        } else {
          this.$emit("error", "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
        }
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>
